<template>
  <div>
    <!-- 인원별 / 업체별 탭 -->
    <vs-tabs :value="tabIndex">
      <vs-tab label="인원별" @click="onChangeTab(0)"></vs-tab>
      <vs-tab label="업체별" @click="onChangeTab(1)"></vs-tab>
    </vs-tabs>

    <!-- 검색 -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          :key="`search-${index}`"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <div class="flex items-center justify-between mb-4 flex-wrap gap-y-2">
      <div class="flex items-center gap-4">
        <vs-select v-model="perPage">
          <vs-select-item text="10개씩 보기" :value="10" />
          <vs-select-item text="20개씩 보기" :value="20" />
          <vs-select-item text="50개씩 보기" :value="50" />
          <vs-select-item text="100개씩 보기" :value="100" />
        </vs-select>
        <span class="total-count">표시된 {{ isPersonTab ? "인원" : "업체" }} : {{ filteredList.length }}{{ isPersonTab ? "명" : "개" }}</span>
      </div>
      <vs-button color="secondary" type="border" @click="onClickDownload">Excel 다운로드</vs-button>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="mb-4">
      <vs-progress indeterminate color="primary" />
    </div>

    <!-- 목록 -->
    <div class="table-wrap" ref="tableWrap">
      <div class="table-scroll">
        <table>
          <colgroup>
            <col style="width: 80px" />
            <col style="width: 140px" />
            <col />
            <col style="width: 160px" />
            <col v-if="isPersonTab" style="width: 120px" />
            <col style="width: 160px" />
          </colgroup>
          <thead>
            <tr>
              <th>연도</th>
              <th>사업장명</th>
              <th>업체명</th>
              <th>사업자등록번호</th>
              <th v-if="isPersonTab">이름</th>
              <th>안전위반통보 누계</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in paginatedList" :key="row.key" :class="{ 'row-active': detail.show && detail.row && detail.row.key === row.key }">
              <td>{{ row.연도 }}</td>
              <td>{{ row.사업장명 }}</td>
              <td>{{ row.업체명 || "-" }}</td>
              <td>{{ row.사업자등록번호 || "-" }}</td>
              <td v-if="isPersonTab">{{ row.이름 }}</td>
              <td class="clickable" @click="openDetail(row, $event)">{{ row.누계 }}회</td>
            </tr>
            <tr v-if="!loading && paginatedList.length === 0">
              <td :colspan="isPersonTab ? 6 : 5">데이터가 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 안전위반통보 누계 상세 팝업 -->
      <div v-if="detail.show" ref="detailPopup" class="detail-popup" :style="detail.style">
        <div class="detail-popup-header">
          <div>
            안전위반통보 누계
            <span class="detail-popup-summary">
              {{ detail.row.누계 }}회 ({{ detail.summaryText }}) · 환산점수 {{ detail.row.점수합 }}점
            </span>
          </div>
          <feather-icon icon="XIcon" svgClasses="h-5 w-5" class="detail-close" title="닫기" @click="closeDetail" />
        </div>
        <div class="detail-popup-body">
          <table class="inner-table">
            <colgroup>
              <col style="width: 110px" />
              <col style="width: 80px" />
              <col style="width: 150px" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>발생일시</th>
                <th>등급</th>
                <th>위반유형</th>
                <th>세부내용</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, itemIndex) in detail.row.details" :key="`detail-${itemIndex}`" :class="gradeRowClass(item.등급)">
                <td>{{ item.발생일자 }}</td>
                <td>
                  <span class="grade-badge" :style="{ background: 등급색상[item.등급] || '#999' }">{{ item.등급 }}</span>
                </td>
                <td>{{ item.유형 }}</td>
                <td class="text-left">{{ item.세부내용 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage" v-model="page"></k-pagination>
      </div>
    </div>

    <popup-excel-download v-model="download" :list="excelList" :title="`인원_업체별 세부 조회(${isPersonTab ? '인원별' : '업체별'})`" />
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";
import { 등급점수, 등급색상 } from "../1229/violationData";

const VIOLATION_BOARD_ID = 1229;

export default {
  components: {
    SearchNav,
    KPagination,
    PopupExcelDownload,
  },
  props: {
    board: Object,
    header: Object,
    permission: Object,
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    isPersonTab() {
      return this.tabIndex === 0;
    },
    stdSearchComponents() {
      const components = [
        {
          component: "search-year",
          label: "연도",
          value: "year",
          query: { y: "year" },
          options: {
            default: new Date().getFullYear(),
            afterFix: "년",
          },
        },
        {
          component: "search-select-field",
          label: "사업장명",
          value: "사업장명",
          query: { f_id: "fieldId", f_name: "fieldName" },
          options: {
            default: "",
          },
        },
        {
          component: "search-start-end",
          label: "집계기간",
          value: "date",
          query: { ss: "startDate", ee: "endDate" },
          options: {
            default: { sd: "", ed: "" },
          },
        },
        {
          component: "search-keyword",
          label: "업체명",
          value: "업체명",
          query: { cn: "keyword" },
          options: {
            default: "",
          },
        },
      ];

      if (this.isPersonTab) {
        components.push({
          component: "search-keyword",
          label: "이름",
          value: "이름",
          query: { nm: "keyword" },
          options: {
            default: "",
          },
        });
      }

      for (const component of components) {
        component.component = searchComponent[component["component"]];
      }
      return components;
    },

    // 원시 데이터 → 개별 통보 건 목록
    allItems() {
      return this.rawData.map((data) => {
        const content = data.postContents ? data.postContents : data.post_content ? JSON.parse(data.post_content) : {};
        const body = content.body || {};
        const 대상자 = body.대상자 || {};
        const 업체 = body.업체 || {};
        return {
          발급일자: body.발급일자 || "",
          발생일자: body.발생일자 || "",
          사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
          구분1: body.구분1 || "",
          구분2: body.구분2 || "",
          대상자id: 대상자.id || 0,
          사번: 대상자.사번 || "",
          이름: 대상자.이름 || "",
          부서명: 대상자.부서명 || "",
          업체명: 업체.업체명 || "",
          사업자등록번호: 업체.사업자등록번호 || "",
          등급: body.등급 || "",
          점수: 등급점수[body.등급] || 0,
          유형: (body.위반내역 && body.위반내역.유형) || "",
          세부내용: body.세부설명 || "",
        };
      });
    },

    // 검색조건 적용된 통보 건 목록
    filteredItems() {
      const search = this.nav.value;
      let list = this.allItems;

      // 집계기간 (승인된 문서에 작성된 발급일자 기준)
      if (search.date && search.date.startDate) {
        list = list.filter((item) => item.발급일자 >= search.date.startDate);
      }
      if (search.date && search.date.endDate) {
        const nextDay = new Date(search.date.endDate).addDays(1).format("yyyy-MM-dd");
        list = list.filter((item) => item.발급일자 < nextDay);
      }

      // 사업장명
      if (search.사업장명 && search.사업장명.fieldName) {
        list = list.filter((item) => item.사업장명.includes(search.사업장명.fieldName));
      }

      // 업체명
      if (search.업체명) {
        list = list.filter((item) => item.업체명.includes(search.업체명));
      }

      // 이름 (인원별 탭)
      if (this.isPersonTab && search.이름) {
        list = list.filter((item) => item.이름.includes(search.이름));
      }

      return list;
    },

    // 인원별/업체별 집계 목록
    filteredList() {
      const map = {};
      for (const item of this.filteredItems) {
        let key = "";
        if (this.isPersonTab) {
          // 인원별: 개별 발부(대상자 있는) 건만
          if (!item.이름) continue;
          key = item.대상자id ? `id${item.대상자id}` : item.사번 || `${item.이름}|${item.업체명}`;
        } else {
          // 업체별: 사업자등록번호 기준 (없으면 업체명)
          key = item.사업자등록번호 || item.업체명;
          if (!key) continue;
        }

        if (!map[key]) {
          map[key] = {
            key,
            연도: this.nav.value.year,
            사업장명: item.사업장명,
            업체명: item.업체명,
            사업자등록번호: item.사업자등록번호,
            이름: item.이름,
            누계: 0,
            점수합: 0,
            details: [],
          };
        }
        const row = map[key];
        if (item.사업장명) row.사업장명 = item.사업장명;
        if (item.업체명) row.업체명 = item.업체명;
        if (item.사업자등록번호) row.사업자등록번호 = item.사업자등록번호;
        row.누계 += 1;
        row.점수합 += item.점수;
        row.details.push({
          발생일자: item.발생일자,
          등급: item.등급,
          유형: item.유형,
          세부내용: item.세부내용,
        });
      }

      const rows = Object.values(map);
      for (const row of rows) {
        row.details.sort((a, b) => {
          return a.발생일자 < b.발생일자 ? 1 : -1;
        });
      }
      rows.sort((a, b) => {
        if (b.누계 !== a.누계) return b.누계 - a.누계;
        return `${a.업체명}`.localeCompare(`${b.업체명}`);
      });
      return rows;
    },

    lastPage() {
      return Math.max(1, Math.ceil(this.filteredList.length / this.perPage));
    },

    paginatedList() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredList.slice(start, start + this.perPage);
    },

    excelList() {
      return this.filteredList.map((row) => {
        const base = {
          "연도": row.연도,
          "사업장명": row.사업장명,
          "업체명": row.업체명 || "-",
          "사업자등록번호": row.사업자등록번호 || "-",
        };
        if (this.isPersonTab) {
          base["이름"] = row.이름;
        }
        base["안전위반통보 누계"] = `${row.누계}회`;
        base["환산점수"] = `${row.점수합}점`;
        return base;
      });
    },
  },
  data() {
    return {
      등급색상,
      tabIndex: 0,
      nav: {
        value: {
          year: new Date().getFullYear(),
        },
      },
      rawData: [],
      loading: false,
      page: 1,
      perPage: 10,
      download: false,
      detail: {
        show: false,
        row: null,
        style: {},
        summaryText: "",
      },
    };
  },
  mounted() {
    // 팝업 외부 클릭 시 닫기
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  watch: {
    "nav.value.year": {
      immediate: true,
      handler() {
        this.loadAllFromApi();
      },
    },
    perPage() {
      this.page = 1;
      this.closeDetail();
    },
    page() {
      this.closeDetail();
    },
    lastPage(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    onChangeTab(index) {
      this.tabIndex = index;
      this.page = 1;
      this.closeDetail();
    },
    onClickSearch() {
      this.page = 1;
      this.closeDetail();
    },
    onDocumentClick(event) {
      if (!this.detail.show) return;
      const popup = this.$refs.detailPopup;
      if (popup && popup.contains(event.target)) return;
      // 누계 셀 클릭은 openDetail 이 토글로 처리하므로 제외
      if (event.target.closest && event.target.closest("td.clickable")) return;
      this.closeDetail();
    },
    onClickDownload() {
      this.download = true;
    },
    gradeRowClass(grade) {
      if (grade === "경미") return "grade-minor";
      if (grade === "주의") return "grade-warning";
      if (grade === "심각") return "grade-severe";
      return "";
    },
    openDetail(row, event) {
      // 같은 행을 다시 클릭하면 닫기
      if (this.detail.show && this.detail.row && this.detail.row.key === row.key) {
        this.closeDetail();
        return;
      }

      const counts = { 경미: 0, 주의: 0, 심각: 0 };
      for (const item of row.details) {
        if (counts[item.등급] !== undefined) counts[item.등급] += 1;
      }
      this.detail.summaryText = ["경미", "주의", "심각"]
        .filter((grade) => counts[grade] > 0)
        .map((grade) => `${grade} ${counts[grade]}건`)
        .join(" · ");

      // 클릭한 셀 바로 아래(가운데 정렬)에 팝업 표시, 테이블 영역을 벗어나지 않게 클램프
      let top = 40;
      let left = 8;
      try {
        const wrapRect = this.$refs.tableWrap.getBoundingClientRect();
        const cellRect = event.currentTarget.getBoundingClientRect();
        const popupWidth = Math.min(620, wrapRect.width - 16);
        top = cellRect.bottom - wrapRect.top + 6;
        left = cellRect.left - wrapRect.left + (cellRect.width / 2) - (popupWidth / 2);
        left = Math.max(8, Math.min(left, wrapRect.width - popupWidth - 8));
      } catch (e) {
        /* ignore */
      }
      this.detail.style = { top: `${top}px`, left: `${left}px` };
      this.detail.row = row;
      this.detail.show = true;
    },
    closeDetail() {
      this.detail.show = false;
      this.detail.row = null;
    },

    // 연도 변경 시에만 API 호출 (결재완료 건만, 연도 귀속은 발급일자 기준 — 1227 집계와 동일, 매년 1월 리셋)
    async loadAllFromApi() {
      this.loading = true;
      this.closeDetail();
      const year = this.nav.value.year;
      try {
        const baseOptions = {
          projectId: this.projectId,
          boardSlug: `board-${VIOLATION_BOARD_ID}`,
          perPage: 200,
          statusIds: [4], // 승인(결재완료)만
          conditions: [{ key: "body.발급일자", operator: "LIKE", value: `${year}-%` }],
          withMeta: true,
          onlyMeta: true,
          metaKeys: ["body.stdInfo.field_name", "body.발급일자", "body.대상자%", "body.업체%", "body.구분1", "body.구분2", "body.등급", "body.위반내역%", "body.세부설명", "body.발생일자"],
        };

        const firstResponse = await this.$store.dispatch("approval/LOAD_POST_ALL", { ...baseOptions, page: 1 });
        let allData = [...firstResponse.data];
        if (firstResponse.last_page > 1) {
          const pagePromises = [];
          for (let p = 2; p <= firstResponse.last_page; p++) {
            pagePromises.push(this.$store.dispatch("approval/LOAD_POST_ALL", { ...baseOptions, page: p }));
          }
          const remainingResponses = await Promise.all(pagePromises);
          for (const response of remainingResponses) {
            allData = allData.concat(response.data);
          }
        }
        this.rawData = allData;
      } catch (e) {
        this.rawData = [];
      }
      this.page = 1;
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.total-count {
  font-size: 13px;
  color: #666;
}
.table-wrap {
  position: relative;
}
.table-scroll {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 12px 7px;
  }

  th {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
  }

  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    background: white;
  }

  .clickable {
    cursor: pointer;
    font-weight: 600;
    color: #5b3cc4;

    &:hover {
      background: #f3f0ff;
    }
  }

  .row-active td {
    background: #f3f0ff;
  }
}

/* 누계 상세 팝업 */
.detail-popup {
  position: absolute;
  /* vuesax 페이지네이션 버튼이 z-index 200 이라 그보다 위로 */
  z-index: 1000;
  width: 620px;
  max-width: calc(100% - 16px);
  background: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  .detail-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    padding: 12px 14px;

    .detail-popup-summary {
      font-size: 12px;
      font-weight: 500;
      color: #777;
      margin-left: 6px;
    }

    .detail-close {
      color: #888;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        color: #ea5455;
      }
    }
  }

  .detail-popup-body {
    padding: 0 14px 14px;
    max-height: 320px;
    overflow-y: auto;
  }

  .inner-table {
    min-width: 0;

    th,
    td {
      padding: 8px 7px;
      font-size: 13px;
      border: 1px solid #e0e0e0;
    }

    .text-left {
      text-align: left;
    }

    .grade-badge {
      display: inline-block;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
      padding: 2px 8px;
    }

    tr.grade-minor td {
      background: #eaf9fc;
    }
    tr.grade-warning td {
      background: #fdf3e6;
    }
    tr.grade-severe td {
      background: #fdecec;
    }
  }
}
</style>
